import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

export interface AdminStatsSectionNavItem {
  label: string;
  route: string;
}

@Component({
  selector: 'admin-stats-section-nav',
  templateUrl: './admin-stats-section-nav.html',
  host: { class: 'block shrink-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminStatsSectionNav {
  readonly items = input.required<AdminStatsSectionNavItem[]>();

  private readonly router = inject(Router);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects.split('?')[0] ?? ''),
      startWith(this.router.url.split('?')[0] ?? ''),
    ),
    { initialValue: this.router.url.split('?')[0] ?? '' },
  );

  readonly activeRoute = computed(() => {
    const url = this.currentUrl();
    const matches = this.items().filter(
      (item) => url === item.route || url.startsWith(`${item.route}/`),
    );
    if (matches.length === 0) {
      return this.items()[0]?.route ?? '';
    }
    return matches.reduce((best, item) => (item.route.length >= best.route.length ? item : best)).route;
  });

  goToItem(item: AdminStatsSectionNavItem): void {
    void this.router.navigate([item.route]);
  }

  selectMobile(event: Event): void {
    const route = (event.target as HTMLSelectElement).value;
    void this.router.navigate([route]);
  }

  getButtonClass(item: AdminStatsSectionNavItem): string {
    const active = this.activeRoute() === item.route;

    if (active) {
      return 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200';
    }

    return 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }
}
